/**
 * useTableSelection - Hook genérico para seleção múltipla em tabelas
 * Reutilizável em todas as telas CRUD com bulk actions
 *
 * @param {Array} items - Array de itens (deve ter propriedade 'id')
 * @param {Object} options - Opções de configuração
 * @param {string} options.idKey - Chave do identificador (default: 'id')
 * @returns {Object} Estado e handlers de seleção
 */

import { useState, useCallback, useMemo } from 'react';

const useTableSelection = (items, options = {}) => {
  const { idKey = 'id' } = options;
  const [selectedIds, setSelectedIds] = useState([]);

  // Toggle seleção de um item
  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => (
      prev.includes(id)
        ? prev.filter((i) => i !== id)
        : [...prev, id]
    ));
  }, []);

  // Selecionar/deselecionar todos
  const selectAll = useCallback((event) => {
    if (event?.target?.checked) {
      setSelectedIds(items.map((item) => item[idKey]));
    } else {
      setSelectedIds([]);
    }
  }, [items, idKey]);

  // Limpar seleção
  const clearSelection = useCallback(() => {
    setSelectedIds([]);
  }, []);

  // Selecionar vários IDs específicos
  const selectMany = useCallback((ids) => {
    setSelectedIds(ids);
  }, []);

  // Verificar se um item está selecionado
  const isSelected = useCallback((id) => selectedIds.includes(id), [selectedIds]);

  // Estados derivados
  const isAllSelected = items.length > 0 && selectedIds.length === items.length;
  const isIndeterminate = selectedIds.length > 0 && selectedIds.length < items.length;
  const hasSelection = selectedIds.length > 0;

  // Items selecionados
  const selectedItems = useMemo(
    () => items.filter((item) => selectedIds.includes(item[idKey])),
    [items, selectedIds, idKey],
  );

  return {
    // IDs e items selecionados
    selectedIds,
    selectedItems,
    selectedCount: selectedIds.length,

    // Handlers
    toggleSelect,
    selectAll,
    selectMany,
    clearSelection,
    isSelected,

    // Estados
    isAllSelected,
    isIndeterminate,
    hasSelection,
  };
};

export default useTableSelection;
